import EntityManage from '@common/components/EntityManage';
import ApiService from '@common/services/ApiService';
import { Button, Paper, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { withStyles } from 'tss-react/mui';
import StyledDivider from '../../../common/components/misc/StyledDivider';
import ErrorToast from './ErrorToast';
import { vcmComponentStatusOptions, vcmStatusOptions } from './options';
import UnitResponseRejectModal from './UnitResponseRejectModal';
import VcmApproveModal from './VcmApproveModal';
import VcmCourseRemovedUnits from './VcmCourseRemovedUnits';
import VcmCourses from './VcmCourses';
import VcmDelegateApproverModal from './VcmDelegateApproverModal';
import VcmDownloadModal from './VcmDownloadModal';
import VcmProfessionalQualifications from './VcmProfessionalQualifications';
import VcmRejectModal from './VcmRejectModal';
import VcmStatusPill from './VcmStatusPill';
import VcmSummary from './VcmSummary';
import VcmUnitCheckModal from './VcmUnitCheckModal';
import VcmUnitResponses from './VcmUnitResponses';
import VcmUnitsFilter from './VcmUnitsFilter';
import VcmUpdateStatusModal from './VcmUpdateStatusModal';
import React from 'react';

const GENERIC_ERROR_MESSAGE = 'Something went wrong, please try again';
const SINGLE_UNIT_ID = 'single-unit';
const FILTER_STATUS_LIST = [
  vcmComponentStatusOptions.in_review.id,
  vcmComponentStatusOptions.approved.id,
  vcmComponentStatusOptions.needs_attention.id
];

const INITIAL_FILTERS = {
  removed_eq: false,
  status_in: []
};

const styles = (theme) => ({
  defaultTabText: {
    color: theme.typography.color.primary,
    opacity: 0.55
  },
  selectedTabText: {
    opacity: 1
  },
  typographyTabText: {
    color: 'inherit',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
    textTransform: 'none'
  },
  vcmDetailsContainer: {
    width: '100%',
    marginTop: theme.typography.pxToRem(25)
  },
  summaryHeadline: {
    marginBottom: theme.typography.pxToRem(16)
  },
  vcmStatus: {
    fontSize: theme.typography.pxToRem(15),
    lineHeight: 1.33,
    textTransform: 'uppercase'
  },
  unitsCountsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px'
  },
  textButton: {
    textTransform: 'none'
  },
  approveButton: {
    backgroundColor: green[600]
  },
  rejectButton: {
    backgroundColor: theme.palette.error.dark
  }
});

class VcmDetails extends EntityManage {
  constructor(props) {
    super(props);
    this.api = new ApiService();
    this.state = {
      vcmId: props.vcmId,
      errorMessage: null,
      vcm: null,
      vcmCourses: [],
      courses: [],
      allUnitResponses: [],
      existingUnitResponses: [],
      allVcmProfessionalQualifications: [],
      removedUnits: [],
      selectedCourseId: null,
      filters: INITIAL_FILTERS,
      params: {},
      refreshTick: 0,
      isRejectModalOpened: false,
      isApproveModalOpened: false,
      isDownloadVcmModalOpened: false,
      isUpdateStatusModalOpened: false,
      isDelegateApproverModalOpened: false,
      activeTab: 'professional-qualifications',
      isLoadingConcerningUnits: false,
      missingUnits: [],
      extraRemovedUnits: [],
      extraPresentUnits: [],
      selectedUnitCodes: [],
      isBatchPending: false,
      unitResponsesForRejectModal: [],
      isUnitResponseRejectModalOpened: false
    };
  }

  componentDidMount() {
    this.getVcmDetailsData();
  }

  getVcmDetailsData = async () => {
    try {
      const getVcmPromise = this.api.get('vcms', this.state.vcmId);
      const getUnitResponsesPromise = this.api.get(
        'vcms',
        this.state.vcmId + '/unit_responses'
      );
      const getVcmCoursesPromise = this.api.get(
        'vcms',
        this.state.vcmId + '/courses'
      );
      const getVcmProfessionalQualificationsPromise = this.api.get(
        'vcms',
        this.state.vcmId + '/professional_qualifications'
      );
      const [vcm, unitResponses, vcmCourses, vcmProfessionalQualifications] =
        await Promise.all([
          getVcmPromise,
          getUnitResponsesPromise,
          getVcmCoursesPromise,
          getVcmProfessionalQualificationsPromise
        ]);

      const coursesAndSingleUnit = vcmCourses.json.data
        .map(({ course }) => course)
        .concat({ id: SINGLE_UNIT_ID, code: 'Single Unit' });

      const allVcmProfessionalQualifications =
        vcmProfessionalQualifications.json.data;
      const selectedCourseId =
        this.state.selectedCourseId || coursesAndSingleUnit[0].id;
      const allUnitResponses = unitResponses.json.data;
      const existingUnitResponses = allUnitResponses.filter(
        (unitResponse) => !unitResponse.removed
      );
      const removedUnits = this.getRemovedUnits({
        selectedCourseId,
        vcmCourses: vcmCourses.json.data,
        allUnitResponses
      });
      this.setState({
        vcm: vcm.json.data,
        vcmCourses: vcmCourses.json.data,
        courses: coursesAndSingleUnit,
        allUnitResponses,
        allVcmProfessionalQualifications,
        removedUnits,
        existingUnitResponses,
        selectedCourseId,
        params: {
          course_id: selectedCourseId === SINGLE_UNIT_ID ? '' : selectedCourseId
        },
        refreshTick: Date.now()
      });
    } catch (error) {
      this.setState({
        errorMessage: GENERIC_ERROR_MESSAGE
      });
    }
  };

  getRemovedUnits = ({ selectedCourseId, vcmCourses, allUnitResponses }) => {
    const vcmCourse = vcmCourses.find(
      (vcmCourse) => vcmCourse.course.id === selectedCourseId
    );

    if (vcmCourse) {
      return allUnitResponses
        .filter(
          (unitResponse) =>
            unitResponse.removed &&
            vcmCourse.unit_ids?.includes(unitResponse.unit.id)
        )
        .map(({ unit }) => unit);
    }

    return [];
  };

  handleTabChange = (_, newValue) => {
    this.setState({ activeTab: newValue });
  };

  handleCourseTabChange = async (_, newValue) => {
    this.setState({
      params: {
        ...this.state.params,
        course_id: newValue === SINGLE_UNIT_ID ? '' : newValue
      },
      selectedUnitCodes: []
    });
    const removedUnits = await this.getRemovedUnits({
      selectedCourseId: newValue,
      allUnitResponses: this.state.allUnitResponses,
      vcmCourses: this.state.vcmCourses
    });
    this.setState((currentState) => ({
      ...currentState,
      selectedCourseId: newValue,
      removedUnits
    }));
  };

  transformFilters = (filters) => {
    return FILTER_STATUS_LIST.reduce((accumulator, key) => {
      accumulator = {
        ...accumulator,
        [key]: filters.status_in.includes(key)
      };
      return accumulator;
    }, {});
  };

  handleApplyFilter = (filters) => {
    const enabledFilters = Object.keys(filters).filter((key) => filters[key]);

    this.setState({
      filters: {
        ...this.state.filters,
        status_in: enabledFilters
      },
      selectedUnitCodes: []
    });
  };

  handleRefresh = () => {
    this.getVcmDetailsData();
  };

  processResponse(res) {
    // Calls the default
    super.processResponse(res);

    // Custom stuff
    if (res.status === 201) {
      this.props.navigate('/vcm/' + res.json.data.id);
    }
  }

  checkVcmThen = async (vcm, subsequentDialogProp) => {
    if (this.state.isLoadingConcerningUnits) {
      return;
    }

    this.setState({ isLoadingConcerningUnits: true });

    try {
      const url = `/api/v1/vcms/${vcm.id}/unit_responses?concerning=1`;
      const res = await this.api.query(url);
      const missingUnits = res.data.missing || [];
      const extra = res.data.extra || { removed_units: [], present_units: [] };
      const extraRemovedUnits = extra.removed_units || [];
      const extraPresentUnits = extra.present_units || [];

      this.setState({ isLoadingConcerningUnits: false, missingUnits, extraRemovedUnits, extraPresentUnits });

      if (missingUnits.length || extraRemovedUnits.length || extraPresentUnits.length) {
        this.setState({ isVcmUnitCheckModalOpened: true });
      }
      else if (subsequentDialogProp) {
        this.setState({ [subsequentDialogProp]: true });
      }
      else {
        this.setState({ isVcmUnitCheckModalOpened: true });
      }
    } catch (error) {
      console.log(error);
      this.setState({ errorMessage: GENERIC_ERROR_MESSAGE });
    } finally {
      this.setState({ isLoadingConcerningUnits: false });
    }
  };

  openRejectModal = (vcm) => {
    this.checkVcmThen(vcm, 'isRejectModalOpened');
  };

  handleCloseRejectModal = () => {
    this.setState({ isRejectModalOpened: false });
  };

  openApproveModal = (vcm) => {
    this.checkVcmThen(vcm, 'isApproveModalOpened');
  };

  handleCloseApproveModal = () => {
    this.setState({ isApproveModalOpened: false });
  };

  openDownloadVcmModal = () => {
    this.setState({ isDownloadVcmModalOpened: true });
  };

  handleCloseDownloadVcmModal = () => {
    this.setState({ isDownloadVcmModalOpened: false });
  };

  openUpdateStatusModal = () => {
    this.setState({ isUpdateStatusModalOpened: true });
  };

  handleCloseUpdateStatusModal = () => {
    this.setState({ isUpdateStatusModalOpened: false });
  };

  openDelegateApproverModal = () => {
    this.setState({ isDelegateApproverModalOpened: true });
  };

  handleCloseDelegateApproverModal = () => {
    this.setState({ isDelegateApproverModalOpened: false });
  };

  openVcmUnitCheckModal = (vcm) => {
    this.checkVcmThen(vcm, null);
  };

  handleCloseVcmUnitCheckModal = () => {
    this.setState({ isVcmUnitCheckModalOpened: false });
  };

  handleUnitSelectionChange = (selectedCodes) => {
    this.setState({ selectedUnitCodes: selectedCodes });
  };

  handleUnitDataLoaded = (data) => {
    this.setState({ allUnitResponses: data.data || [] });
  };

  handleBatchApprove = async () => {
    const { selectedUnitCodes, vcm } = this.state;

    if (selectedUnitCodes.length === 0 || vcm.status !== 'in_review') {
      return;
    }

    this.setState({ isBatchPending: true });

    try {
      const url = `/api/v1/vcms/${vcm.id}/bulk_approve_units`;
      const res = await this.api.put(url, { unit_codes: selectedUnitCodes });

      if (res.status >= 200 && res.status < 400) {
        this.handleRefresh();
        this.setState({ selectedUnitCodes: [] });
      } else {
        this.setState({ errorMessage: res.json?.message || 'Failed to approve units' });
      }
    } catch (error) {
      this.setState({ errorMessage: 'Something went wrong, please try again' });
    } finally {
      this.setState({ isBatchPending: false });
    }
  };

  handleBatchReject = () => {
    const { selectedUnitCodes, allUnitResponses } = this.state;

    if (selectedUnitCodes.length > 0) {
      const selectedResponses = allUnitResponses.filter(ur => selectedUnitCodes.includes(ur.unit.code));

      if (selectedResponses.length > 0) {
        this.setState({
          unitResponsesForRejectModal: selectedResponses,
          isUnitResponseRejectModalOpened: true
        });
      }
    }
  };

  handleCloseUnitResponseRejectModal = () => {
    this.setState({ unitResponsesForRejectModal: [], isUnitResponseRejectModalOpened: false, selectedUnitCodes: [] });
  };

  render() {
    const { classes, user } = this.props;
    const {
      vcm,
      filters,
      params,
      existingUnitResponses,
      allVcmProfessionalQualifications,
      courses,
      selectedCourseId,
      refreshTick,
      removedUnits,
      isRejectModalOpened,
      isApproveModalOpened,
      isDownloadVcmModalOpened,
      isUpdateStatusModalOpened,
      isDelegateApproverModalOpened,
      isVcmUnitCheckModalOpened,
      errorMessage,
      activeTab,
      isLoadingConcerningUnits,
      missingUnits,
      extraRemovedUnits,
      extraPresentUnits,
      selectedUnitCodes,
      isBatchPending,
      unitResponsesForRejectModal,
      isUnitResponseRejectModalOpened
    } = this.state;

    if (!vcm) {
      return null;
    }

    let canApprove = false;
    let canReject = false;
    if (
      existingUnitResponses.every(
        (unitResponse) =>
          unitResponse.status !== 'in_review' && vcm.status === 'in_review'
      ) &&
      allVcmProfessionalQualifications.every(
        (vcmProfessionalQualification) =>
          vcmProfessionalQualification.status !== 'in_review' &&
          vcm.status === 'in_review'
      )
    ) {
      canApprove =
        existingUnitResponses.every(
          (unitResponse) => unitResponse.status === 'approved'
        ) &&
        allVcmProfessionalQualifications.every(
          (vcmProfessionalQualification) =>
            vcmProfessionalQualification.status === 'approved'
        );
      canReject = !canApprove;
    }

    const vcmStatusText = vcmStatusOptions[vcm.status]?.label ?? vcm.status;
    return (
      <>
        <Grid container className={classes.vcmDetailsContainer} spacing={2}>
          <Grid size={12}>
            <VcmStatusPill status={vcm.status} />
          </Grid>
          <Grid size={3}>
            <Typography className={classes.summaryHeadline} variant="h5">
              VCM - {vcm.owner_user.full_name}
            </Typography>
          </Grid>
          <Grid
            size={9}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-start"
            gap={3}
          >
            <div>
              <Button
                className={classes.textButton}
                onClick={this.openDelegateApproverModal}
              >
                Delegate Approver
              </Button>
              {
                (user.is_any_admin) &&
                <Button
                  className={classes.textButton}
                  onClick={() => { this.openVcmUnitCheckModal(vcm); }}
                  disabled={isLoadingConcerningUnits}
                >
                  VCM Unit Check
                </Button>
              }
              {
                (user.is_any_admin) && <Button
                  className={classes.textButton}
                  onClick={this.openUpdateStatusModal}
                >
                  Update Status
                </Button>
              }
              <Button
                component={Link}
                to={`/vcm/${vcm.id}/history`}
                className={classes.textButton}
              >
                History
              </Button>
              <Button
                className={classes.textButton}
                onClick={this.openDownloadVcmModal}
              >
                Download VCM
              </Button>
            </div>
            <Button
              disabled={vcm.status !== 'in_review' || isLoadingConcerningUnits}
              variant={canReject ? 'contained' : 'outlined'}
              onClick={() => { this.openRejectModal(vcm); }}
            >
              Return VCM To Educator
            </Button>
            <Button
              disabled={vcm.status !== 'in_review' || isLoadingConcerningUnits}
              variant={canApprove ? 'contained' : 'outlined'}
              onClick={() => { this.openApproveModal(vcm); }}
            >
              Approve VCM
            </Button>
          </Grid>
          <Grid size={12}>
            <VcmSummary
              vcm={vcm}
              classes={classes}
              unitResponses={existingUnitResponses}
              vcmQualifications={allVcmProfessionalQualifications}
            />
          </Grid>
          <Grid size={12} my={2}>
            <Tabs
              onChange={this.handleTabChange}
              textColor="secondary"
              indicatorColor="secondary"
              value={activeTab}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                value="professional-qualifications"
                classes={{
                  root: classes.defaultTabText,
                  selected: classes.selectedTabText
                }}
                label={
                  <Typography className={classes.typographyTabText}>
                    Professional Qualifications
                  </Typography>
                }
              />
              <Tab
                value="courses"
                classes={{
                  root: classes.defaultTabText,
                  selected: classes.selectedTabText
                }}
                label={
                  <Typography className={classes.typographyTabText}>
                    Courses
                  </Typography>
                }
              />
            </Tabs>
            <StyledDivider />
          </Grid>
          {activeTab === 'professional-qualifications' ? (
            <>
              <Grid size={12}>
                <Paper>
                  <VcmProfessionalQualifications
                    vcm={vcm}
                    institute={this.props.institute}
                    refreshTick={refreshTick}
                    onRefresh={this.handleRefresh}
                  />
                </Paper>
              </Grid>
            </>
          ) : null}
          {activeTab === 'courses' ? (
            <>
              <Grid size={12}>
                <Paper>
                  <VcmUnitsFilter
                    initialFilters={this.transformFilters(INITIAL_FILTERS)}
                    onApplyFilters={this.handleApplyFilter}
                  />
                </Paper>
              </Grid>
              <Grid size={12} mt={2} display="flex" alignItems="center" justifyContent="space-between">
                <div style={{ flex: 1 }}>
                  <VcmCourses
                    courses={courses}
                    onTabChange={this.handleCourseTabChange}
                    selectedCourseId={selectedCourseId}
                  />
                </div>
                {selectedUnitCodes.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                    <Button
                      disabled={isBatchPending || vcm.status !== 'in_review'}
                      onClick={this.handleBatchApprove}
                      color="secondary"
                      size="small"
                      variant="contained"
                      className={`${classes.actionButtons} ${classes.approveButton}`}
                    >
                      <Typography variant="button">
                        Approve ({selectedUnitCodes.length})
                      </Typography>
                    </Button>
                    <Button
                      disabled={isBatchPending || vcm.status !== 'in_review'}
                      onClick={this.handleBatchReject}
                      color="secondary"
                      size="small"
                      variant="contained"
                      className={`${classes.actionButtons} ${classes.rejectButton}`}
                    >
                      <Typography variant="button">
                        Mark For Attention ({selectedUnitCodes.length})
                      </Typography>
                    </Button>
                  </div>
                )}
              </Grid>
              <Grid size={12}>
                <Paper>
                  <VcmUnitResponses
                    vcm={vcm}
                    institute={this.props.institute}
                    refreshTick={refreshTick}
                    filters={filters}
                    params={params}
                    onRefresh={this.handleRefresh}
                    selectedCodes={selectedUnitCodes}
                    onSelectionChange={this.handleUnitSelectionChange}
                    onDataLoaded={this.handleUnitDataLoaded}
                    isPending={isBatchPending}
                  />
                  <VcmCourseRemovedUnits units={removedUnits} />
                </Paper>
              </Grid>
            </>
          ) : null}
        </Grid>
        <VcmRejectModal
          isOpened={isRejectModalOpened}
          onClose={this.handleCloseRejectModal}
          unitResponses={existingUnitResponses}
          vcmProfessionalQualifications={allVcmProfessionalQualifications}
          vcm={vcm}
          onRefresh={this.handleRefresh}
        />
        <VcmApproveModal
          isOpened={isApproveModalOpened}
          onClose={this.handleCloseApproveModal}
          unitResponses={existingUnitResponses}
          vcmProfessionalQualifications={allVcmProfessionalQualifications}
          vcm={vcm}
          onRefresh={this.handleRefresh}
        />
        <VcmUpdateStatusModal
          isOpened={isUpdateStatusModalOpened}
          onClose={this.handleCloseUpdateStatusModal}
          vcm={vcm}
          onRefresh={this.handleRefresh}
        />
        <VcmDownloadModal
          courses={courses}
          unitResponses={existingUnitResponses}
          isOpened={isDownloadVcmModalOpened}
          onClose={this.handleCloseDownloadVcmModal}
          vcm={vcm}
        />
        <VcmDelegateApproverModal
          isOpened={isDelegateApproverModalOpened}
          onClose={this.handleCloseDelegateApproverModal}
          vcm={vcm}
          onRefresh={this.handleRefresh}
        />
        <VcmUnitCheckModal
          isOpened={isVcmUnitCheckModalOpened}
          onClose={this.handleCloseVcmUnitCheckModal}
          missingUnits={missingUnits}
          extraRemovedUnits={extraRemovedUnits}
          extraPresentUnits={extraPresentUnits}
          vcm={vcm}
          onRefresh={this.handleRefresh}
        />
        <UnitResponseRejectModal
          isOpened={isUnitResponseRejectModalOpened}
          onClose={this.handleCloseUnitResponseRejectModal}
          unitResponses={this.state.unitResponsesForRejectModal}
          vcm={vcm}
          onRefresh={this.handleRefresh}
        />
        <ErrorToast
          open={!!errorMessage}
          errorMessage={errorMessage}
          onClose={() => this.setState({ errorMessage: null })}
        />
      </>
    );
  }
}

function VcmDetailsWrapper(props) {
  const navigate = useNavigate();
  const params = useParams();
  return <VcmDetails {...props} navigate={navigate} vcmId={params.id} />;
}

export default withStyles(VcmDetailsWrapper, styles, { withTheme: true });
